/**
 * A promise-chaining mutex. `runExclusive(operation)` runs `operation` only after every
 * previously enqueued operation on the same mutex has fully settled, so a read-modify-write
 * that spans one or more awaits cannot interleave with another. Each call to {@link createMutex}
 * returns an independent `runExclusive` closed over its own queue.
 *
 * A failed operation still releases the lock, so one caller's rejection cannot poison the next;
 * the rejection itself propagates to that caller as normal.
 *
 * Not reentrant: calling `runExclusive` from inside an operation on the same mutex deadlocks.
 */
export function createMutex(): <T>(operation: () => Promise<T>) => Promise<T> {
  let queue: Promise<void> = Promise.resolve();

  return (operation) => {
    const result = queue.then(() => operation());
    queue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  };
}
